import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatDistanceToNowStrict } from 'date-fns';

import { globalAdminAPI } from '../../services/api';

const getFilenameFromDisposition = (headerValue, fallback = 'case-transfer.zip') => {
  const header = (headerValue || '').toString();
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = header.match(/filename="?([^"]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return fallback;
};

const formatUpdatedAt = (value) => {
  if (!value) {
    return 'Unknown';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return `${formatDistanceToNowStrict(parsed)} ago`;
};

const renderEnvironmentChips = (environments = {}) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
    <Chip
      size="small"
      color={environments?.testing?.published ? 'warning' : 'default'}
      label={environments?.testing?.published ? 'Testing published' : 'Testing draft'}
    />
    <Chip
      size="small"
      color={environments?.production?.published ? 'success' : 'default'}
      label={environments?.production?.published ? 'Production published' : 'Production draft'}
    />
  </Stack>
);

const CaseTransferManager = () => {
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [importForm, setImportForm] = useState({
    file: null,
    targetEnvironment: 'production',
    conflictStrategy: 'error',
  });
  const [feedback, setFeedback] = useState(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const response = await globalAdminAPI.listTransferCases({
        search: search.trim() || undefined,
      });
      setCases(response.data?.cases || []);
      setFeedback((prev) => (prev?.type === 'error' ? null : prev));
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Unable to load official cases for transfer.',
      });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadCases();
  }, [loadCases, refreshKey]);

  const sortedCases = useMemo(
    () => [...cases].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()),
    [cases],
  );

  const handleDownload = async (caseId, title) => {
    setDownloadingId(caseId);
    try {
      const response = await globalAdminAPI.exportTransferCase(caseId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/zip' });
      const filename = getFilenameFromDisposition(response.headers?.['content-disposition'], `${title || 'case'}-transfer.zip`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setFeedback({
        type: 'success',
        message: `Downloaded transfer package for "${title}".`,
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to export the case transfer package.',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (caseId, title) => {
    const confirmed = window.confirm(
      `Delete "${title}" from the official library? This performs a soft delete so the case will disappear from the case manager and future library queries.`
    );
    if (!confirmed) {
      return;
    }

    setDeletingId(caseId);
    try {
      await globalAdminAPI.deleteTransferCase(caseId);
      setCases((prev) => prev.filter((entry) => entry.id !== caseId));
      setFeedback({
        type: 'success',
        message: `Deleted "${title}".`,
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to delete the official case.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleImportFieldChange = (field) => (event) => {
    const value = field === 'file' ? event.target.files?.[0] || null : event.target.value;
    setImportForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'file') {
      event.target.value = null;
    }
  };

  const handleImport = async () => {
    if (!importForm.file) {
      setFeedback({
        type: 'error',
        message: 'Choose a transfer package zip file first.',
      });
      return;
    }

    setImporting(true);
    try {
      const response = await globalAdminAPI.importTransferCase(importForm);
      const detail = response.data || {};
      setImportForm((prev) => ({
        ...prev,
        file: null,
      }));
      setFeedback({
        type: 'success',
        message: `Imported "${detail.title}" into ${detail.targetEnvironment}.`,
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to import the transfer package.',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Export a case package from the source environment, then open the admin panel in the target environment and import that same zip.
        This flow remaps characters by `addressableKey`/name, recreates bundled media files, and prevents raw database IDs from leaking across environments.
      </Alert>

      {feedback && (
        <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">Export From This Database</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Only official-library cases are shown here.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setRefreshKey((prev) => prev + 1)}
                >
                  Refresh
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Search cases"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={loadCases}
                  sx={{ minWidth: 140 }}
                >
                  Search
                </Button>
              </Stack>

              {loading ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Environments</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCases.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{entry.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {entry.presentingComplaint} | {entry.specialty} | {entry.difficulty}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.character?.name || 'No character'}{entry.isDraft ? ' | Draft' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.version || 1}</TableCell>
                        <TableCell>{renderEnvironmentChips(entry.environments)}</TableCell>
                        <TableCell>{formatUpdatedAt(entry.updatedAt)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={downloadingId === entry.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                              disabled={downloadingId === entry.id || deletingId === entry.id}
                              onClick={() => handleDownload(entry.id, entry.title)}
                            >
                              {downloadingId === entry.id ? 'Exporting...' : 'Download'}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={deletingId === entry.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                              disabled={deletingId === entry.id || downloadingId === entry.id}
                              onClick={() => handleDelete(entry.id, entry.title)}
                            >
                              {deletingId === entry.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!sortedCases.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No official cases matched the current search.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">Import Into This Database</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use this in the target environment after downloading a package elsewhere.
                </Typography>
              </Box>

              <TextField
                select
                label="Target environment"
                value={importForm.targetEnvironment}
                onChange={handleImportFieldChange('targetEnvironment')}
                fullWidth
              >
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
              </TextField>

              <TextField
                select
                label="Title conflict handling"
                value={importForm.conflictStrategy}
                onChange={handleImportFieldChange('conflictStrategy')}
                helperText={importForm.conflictStrategy === 'suffix'
                  ? 'Adds a numeric suffix if the title already exists.'
                  : 'Stops the import if an official case with the same title already exists.'}
                fullWidth
              >
                <MenuItem value="error">Fail on duplicate title</MenuItem>
                <MenuItem value="suffix">Create a suffixed copy</MenuItem>
              </TextField>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                >
                  Choose zip
                  <input hidden type="file" accept=".zip,application/zip" onChange={handleImportFieldChange('file')} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {importForm.file ? importForm.file.name : 'No file selected'}
                </Typography>
              </Stack>

              <Alert severity="warning">
                Imports will fail if the target database does not already have a matching active character.
              </Alert>

              <Button
                variant="contained"
                onClick={handleImport}
                disabled={importing}
                startIcon={importing ? <CircularProgress size={18} /> : <UploadFileIcon />}
              >
                {importing ? 'Importing...' : 'Import package'}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CaseTransferManager;
